import { html, css, LitElement } from 'lit-element';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import '@material/mwc-button';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-checkbox';
import '@material/mwc-formfield';

import { db, functions } from './intrivity_app.js';
import { hasRole } from './utility.js';
import { t, Localized } from './i18n.js';

// Standalone "Edit User" dialog, shared by the per-group Users list
// (intrivity-admin-users.js) and the global Users search
// (admin_users.js). Operates directly on the users/{email} doc, so it
// doesn't need a group context — only group-scoped roles (managed via
// the separate "Roles" dialog in intrivity-admin-users.js) are excluded.
//
// The user's own roles (users/{email}.role) are editable only when the host
// sets rolesEditable -- the global Users search does, the per-group list does
// not. Elsewhere the checkboxes are shown disabled and Save writes back the
// roles exactly as they were loaded.
export class IntrivityUserEditDialog extends Localized(LitElement) {

  static get properties() {
    return {
      user_profile: { type: Object },
      open: { type: Boolean },
      editingUser: { type: Object },
      rolesEditable: { type: Boolean },
      _formData: { type: Object },
    };
  }

  constructor() {
    super();
    this.open = false;
    this.editingUser = null;
    this.rolesEditable = false;
    this._formData = {};
    this._loadedRole = undefined;
    this._loadedForEmail = null;
  }

  // editingUser is whatever snapshot the caller happened to have lying
  // around -- a global search result (admin_users.js) or a group's member
  // list (intrivity-admin-users.js), neither of which refreshes while just
  // sitting open. Re-reading the doc fresh every time the dialog opens means
  // Save can never write back a stale profile/groups/role over something
  // that changed server-side since that list was loaded.
  //
  // Keyed on open transitioning to true (not just editingUser changing
  // reference) so reopening on the *same* selected user still refetches --
  // Lit skips a property update entirely when the reference hasn't changed.
  updated(changedProps) {
    if (this.open && this.editingUser && this.editingUser.email
        && this._loadedForEmail !== this.editingUser.email) {
      this._loadedForEmail = this.editingUser.email;
      this._loadFreshUser();
    }
    if (changedProps.has('open') && !this.open) {
      this._loadedForEmail = null;
    }
  }

  async _loadFreshUser() {
    const email = this.editingUser.email;
    try {
      const snap = await getDoc(doc(db, 'users', email));
      this._formData = snap.exists() ? { ...snap.data() } : { ...this.editingUser };
    } catch (e) {
      console.error('Could not load fresh user data, using the passed-in snapshot:', e);
      this._formData = { ...this.editingUser };
    }
    this._loadedRole = Array.isArray(this._formData.role) ? [...this._formData.role] : this._formData.role;
  }

  // Outside the Users search the roles are read-only: whatever the form holds,
  // write back the roles as they were loaded.
  _lockRoles(user) {
    if (this.rolesEditable) return;
    if (this._loadedRole === undefined) delete user.role;
    else user.role = this._loadedRole;
  }

  _handleFormInput(e) {
    const target = e.target;
    let { name, value } = target;

    if (!name) {
      name = target.dataset.name;
    }

    if (typeof value === 'string') {
      value = value.trim();
    }

    if (name === 'paid' || name === 'process') {
      if (value === "true") {
        value = true;
      } else if (value === "false") {
        value = false;
      } else {
        value = undefined;
      }
    }

    this._formData = { ...this._formData, [name]: value };

    if (name === 'paid' && value === true) {
      this._formData.paid_by_administrator = new Date().toISOString();
    }
    if (name === 'paid' && value !== true) {
      delete this._formData.paid_by_administrator;
    }
  }

  _toggleRole(role, e) {
    const checked = e.target.checked;
    const currentRoles = new Set(this._formData.role || []);
    if (checked) {
      currentRoles.add(role);
    } else {
      currentRoles.delete(role);
    }
    this._formData = { ...this._formData, role: Array.from(currentRoles) };
  }

  async _save() {
    if (!hasRole('superadmin', this.user_profile)) return;
    const user = { ...this._formData };
    // editingUser may be a search-result object (intrivity-user-search.js
    // tags every match with _id for the list to key/select on) rather than
    // a plain doc read -- _id was never a real field and must not be
    // written back onto the document.
    delete user._id;
    this._lockRoles(user);
    await setDoc(doc(db, "users", user.email), user);
    alert(t('userEdit.saved', { name: user.fullName }));
    this.dispatchEvent(new CustomEvent('user-saved', { detail: { user }, bubbles: true, composed: true }));
    this._close();
  }

  async _reset() {
    if (!hasRole('superadmin', this.user_profile)) return;
    if (!confirm(t('userEdit.confirmReset', { name: this._formData.fullName }))) return;

    // Server-side: Storage rules deny the browser any delete under reports/ or
    // surveymonkey_responses/, and the function also resets the profile and groups.
    try {
      await httpsCallable(functions, 'resetUserSurvey')({ email: this._formData.email });
    } catch (error) {
      console.error('Error resetting survey:', error);
      alert(t('common.genericError'));
      return;
    }
    const user = { ...this._formData };
    delete user.survey_id;
    delete user.profile;
    user.process = false;
    user.processing = false;

    alert(t('userEdit.reset', { name: user.fullName }));
    this.dispatchEvent(new CustomEvent('user-saved', { detail: { user }, bubbles: true, composed: true }));
    this._close();
  }

  _close() {
    this.open = false;
    this.dispatchEvent(new CustomEvent('dialog-closed', { bubbles: true, composed: true }));
  }

  render() {
    return html`
      <mwc-dialog heading=${t('userEdit.heading')} ?open=${this.open} @closed=${() => this._close()}>
        <div style="width: 100%; display: grid; grid-template-columns: 1fr 1fr;">
            <mwc-textfield style="grid-column: span 2;" label=${t('userEdit.displayName')} name="displayName" .value=${this._formData.displayName ? this._formData.displayName: ''} @input=${(e) => this._handleFormInput(e)}></mwc-textfield>
            <mwc-textfield required label=${t('common.firstName')} name="firstName" .value=${this._formData.firstName ? this._formData.firstName: ''} @input=${(e) => this._handleFormInput(e)}></mwc-textfield>
            <mwc-textfield required label=${t('common.lastName')} name="lastName" .value=${this._formData.lastName ? this._formData.lastName: ''} @input=${(e) => this._handleFormInput(e)}></mwc-textfield>
            <mwc-textfield required disabled style="grid-column: span 2;" label=${t('login.email')} name="email" .value=${this._formData.email || ''} @input=${(e) => this._handleFormInput(e)}></mwc-textfield>
            <mwc-textarea style="grid-column: span 2;" label=${t('common.description')} name="description" .value=${this._formData.description ? this._formData.description: '' } @input=${(e) => this._handleFormInput(e)}></mwc-textarea>
            <mwc-select data-name="paid" name="paid" label=${t('userEdit.paid')}
                .value=${this._formData.paid !== undefined ? String(this._formData.paid) : ""} @selected=${(e) => this._handleFormInput(e)} @closed=${e => e.stopPropagation()}>
                    <mwc-list-item value="true" ?selected=${this._formData.paid === true}>${t('common.yes')}</mwc-list-item>
                    <mwc-list-item value="false" ?selected=${this._formData.paid === false}>${t('common.no')}</mwc-list-item>
            </mwc-select>
            <mwc-select data-name="process" name="process" label=${t('userEdit.process')}
                .value=${this._formData.process !== undefined ? String(this._formData.process) : ""} @selected=${(e) => this._handleFormInput(e)} @closed=${e => e.stopPropagation()}>
                    <mwc-list-item value="true" ?selected=${this._formData.process === true}>${t('common.yes')}</mwc-list-item>
                    <mwc-list-item value="false" ?selected=${this._formData.process === false}>${t('common.no')}</mwc-list-item>
            </mwc-select>
            <mwc-textfield required style="grid-column: span 2;" disabled label=${t('userEdit.homeGroup')} name="group" .value=${this._formData.group ? this._formData.group: ''} @input=${(e) => this._handleFormInput(e)}></mwc-textfield>
            ${hasRole('superadmin', this.user_profile) ? html`
            <div style="grid-column: span 2; margin-top: 12px;">${t('userEdit.userRoles')}</div>
            <div class="roles-row">
              <mwc-formfield label=${t('role.trainer')}>
                <mwc-checkbox
                  ?disabled=${!this.rolesEditable}
                  @change=${e => this._toggleRole('trainer', e)}
                  .checked=${(this._formData.role || []).includes('trainer')}>
                </mwc-checkbox>
              </mwc-formfield>

              <mwc-formfield label=${t('role.programManager')}>
                <mwc-checkbox
                  ?disabled=${!this.rolesEditable}
                  @change=${e => this._toggleRole('program_manager', e)}
                  .checked=${(this._formData.role || []).includes('program_manager')}>
                </mwc-checkbox>
              </mwc-formfield>

              <mwc-formfield label=${t('role.admin')}>
                <mwc-checkbox
                  ?disabled=${!this.rolesEditable}
                  @change=${e => this._toggleRole('admin', e)}
                  .checked=${(this._formData.role || []).includes('admin')}>
                </mwc-checkbox>
              </mwc-formfield>

              <mwc-formfield label=${t('role.superadmin')}>
                <mwc-checkbox
                  ?disabled=${!this.rolesEditable}
                  @change=${e => this._toggleRole('superadmin', e)}
                  .checked=${(this._formData.role || []).includes('superadmin')}>
                </mwc-checkbox>
              </mwc-formfield>
            </div>
            `: html``}
        </div>
        ${hasRole('superadmin', this.user_profile) ? html`
            <mwc-button @click=${() => this._reset()} label=${t('userEdit.resetSurvey')}></mwc-button>
        `: html``}
        <mwc-button ?disabled=${!hasRole('superadmin', this.user_profile)} slot="primaryAction" label=${t('common.save')} @click=${() => this._save()}></mwc-button>
        <mwc-button slot="secondaryAction" dialogAction="cancel" label=${t('common.cancel')}></mwc-button>
      </mwc-dialog>
    `;
  }
}
if (!customElements.get('intrivity-user-edit-dialog')) customElements.define('intrivity-user-edit-dialog', IntrivityUserEditDialog);
